import { SafeError, SafeType, SafePromise } from '@benzinga/safe-await';
import { Category, Source, SourceGroup, SourceId } from './entities';
import { StringNumber } from '@benzinga/session';
import { sortByString, Optional } from '@benzinga/utils';

export type PressReleaseId = SourceId;

export interface ContentType {
  description: string;
  full_name: string;
  name: string;
  short_name: string;
  type: SourceId;
  group: string;
  weight: number;
}

export interface ContentGroup {
  id: string;
  name: string;
  weight: number;
}

export type PressRelease = PressReleaseId;

interface IncomingTaxonomy {
  description: string;
  help: string;
  hierarchy: '0' | '1';
  module: string;
  multiple: '0' | '1';
  name: 'Channels';
  nodes: Record<SourceId, SourceId>;
  relations: '0' | '1';
  required: '0' | '1';
  tags: '0' | '1';
  vid: StringNumber;
}

interface IncomingTaxonomies {
  [vid: number]: IncomingTaxonomy;
}

interface Term {
  depth: number;
  description: string;
  name: string;
  parents: number[];
  tid: number;
  vid: number;
  weight: number;
}

interface IncomingTerms {
  1: Term[];
  12: Term[];
  15: Term[];
}
export interface ContentTypesForPro {
  pr_types: PressRelease[];
  sponsored_types: string[];
  taxonomies: IncomingTaxonomies;
  terms: IncomingTerms;
  content_types: ContentType[];
  content_groups: ContentGroup[];
}

export interface ContentTypes {
  categories: Category[];
  pressRelease: PressRelease[];
  sources: Source[];
  sourcesGroups: SourceGroup[];
}

export const ingressSources = (ingressContentType: ContentTypesForPro): SafeType<Source[]> => {
  if (ingressContentType?.content_types) {
    return {
      ok: Object.values(ingressContentType.content_types).map(Source => ({
        description: Source.description,
        fullName: Source.full_name,
        group: Source.group,
        id: Source.type,
        name: Source.name,
        shortName: Source.short_name,
        weight: Source.weight,
      })),
    };
  } else {
    return { err: new SafeError('did not get a valid Source', 'invalid_Source') };
  }
};

export interface ContentTypes {
  categories: Category[];
  pressRelease: PressRelease[];
  sources: Source[];
  sourcesGroups: SourceGroup[];
  sponsored: string[];
}

export const ingressSourcesGroup = (ingressContentType: ContentTypesForPro): SafeType<SourceGroup[]> => {
  if (ingressContentType?.content_groups) {
    return {
      ok: Object.values(ingressContentType.content_groups).map(SourceGroup => ({
        id: SourceGroup.id,
        name: SourceGroup.name,
        weight: SourceGroup.weight,
      })),
    };
  } else {
    return { err: new SafeError('did not get a valid Source Groups', 'invalid_Source') };
  }
};

export const ingressPressReleases = (ingressContentType: ContentTypesForPro): SafeType<PressRelease[]> => {
  if (ingressContentType?.pr_types) {
    return { ok: ingressContentType.pr_types };
  } else {
    return { err: new SafeError('did not get a valid Source', 'invalid_Source') };
  }
};

export const ingressSponsoredTypes = (ingressContentType: ContentTypesForPro): SafeType<PressRelease[]> => {
  if (ingressContentType?.sponsored_types) {
    return { ok: ingressContentType.sponsored_types };
  } else {
    return { err: new SafeError('did not get a valid Source', 'invalid_Source') };
  }
};

const createCategoriesTreesFrom = (categories: Optional<Category, 'children'>[], parent = 0): Category[] => {
  const roots = categories.filter(category => category.parents[0] === parent);

  const storedCategories = roots.reduce<Category[]>((acc, root) => {
    const subCategories = categories.filter(subCategory => subCategory.parents[0] === root.tid);
    const processedSubCategories = createCategoriesTreesFrom(subCategories, root.tid);
    return acc.concat([
      {
        ...root,
        children: processedSubCategories.sort((a, b) => sortByString(a.name, b.name)),
      },
    ]);
  }, []);

  return storedCategories.sort((a, b) => sortByString(a.name, b.name));
};

const ingressCategories = (ingressContentType: ContentTypesForPro): SafeType<Category[]> => {
  const terms = ingressContentType.terms?.['1'];
  const newCategories = terms.map(category => ({
    ...{ id: category.tid.toString() },
    ...category,
  }));
  return { ok: createCategoriesTreesFrom(newCategories) };
};

export const ingressContextTypes = async (
  ingressContentType: SafePromise<ContentTypesForPro>,
): SafePromise<ContentTypes> => {
  const contentType = await ingressContentType;
  if (contentType.err) {
    return { err: contentType.err };
  }
  const categories = ingressCategories(contentType.ok);
  if (categories.err) {
    return { err: categories.err };
  }
  const sources = ingressSources(contentType.ok);
  if (sources.err) {
    return { err: sources.err };
  }
  const sourcesGroup = ingressSourcesGroup(contentType.ok);
  if (sourcesGroup.err) {
    return { err: sourcesGroup.err };
  }
  const pressRelease = ingressPressReleases(contentType.ok);
  if (pressRelease.err) {
    return { err: pressRelease.err };
  }
  const sponsored = ingressSponsoredTypes(contentType.ok);
  if (sponsored.err) {
    return { err: sponsored.err };
  }
  return {
    ok: {
      categories: categories.ok,
      pressRelease: pressRelease.ok,
      sources: sources.ok,
      sourcesGroups: sourcesGroup.ok,
      sponsored: sponsored.ok,
    },
  };
};
